import React, { Component } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import DocumentTitle from "react-document-title";

import * as actions from "./../../actions/index";
import { ApiConfig, MetaTitle, API_URL } from "./../../settings";
import {
    selectSearchTerm,
    selectUserLocation,
    selectFilterItems,
    selectSearchResult,
    selectChurches,
    selectNextPage,
    selectTotalAvailable,
    selectFetchStatus,
    selectFilters,
    selectEraContent,
    selectChurchesNear,
    selectLatestFetchType,
    selectSearchError,
} from "./../../selectors.js";

// Components
import "./style.css";
import BigSpacer from "./../../components/BigSpacer";
import MediumSpacer from "./../../components/MediumSpacer";
import SearchResult from "./../../components/SearchResult";
import Button from "./../../components/Button";
import EraBox from "./../../components/EraBox";

import SearchChurch from "./../../images/search-church.svg";
import { updateCanonicalLink } from "../../helpers/helpers.js";

const SEARCH_DEBOUNCE_MS = 400;

class Search extends Component {
    constructor() {
        super();

        this.state = {
            churchData: null,
            loading: false,
            loadingMore: false,
        };

        this._searchDebounceTimer = null;
        this._cancelTokenSource = null;

        this.loadMore = this.loadMore.bind(this);
    }

    // ================================
    // React lifesycle methods
    // ================================

    componentDidMount() {
        const {
            searchResult,
            searchTerm,
            churches,
            filterItems,
            fetchAgain,
            history,
            filters,
            churchesNear,
            latestFetchType,
        } = this.props;

        // If there is a query in the url, and filter-object is loaded
        if (history.location.search !== "" && filters) {
            this.findEraFromUrl();
        }

        if (fetchAgain && searchTerm && !filterItems.length) {
            // Fetch only searchTerm
            this.getChurchesByName(searchTerm, false, true, false, false);
        } else if (fetchAgain && filterItems.length >= 0) {
            // Fetch again, but
            this.getChurchesByName(searchTerm, filterItems, true);
        } else if (searchResult || churchesNear) {
            // no new terms but result is present
            if (latestFetchType === "location")
                this.setState({ churchData: churchesNear });
            if (latestFetchType === "search")
                this.setState({ churchData: churches });
        }
        updateCanonicalLink();
    }

    componentDidUpdate(prevProps) {
        // Search term typed by the user
        const prevSearchTerm = prevProps.searchTerm;
        const thisSearchTerm = this.props.searchTerm;

        // Filters selected by the user
        const prevFilterItems = prevProps.filterItems;
        const thisFilterItems = this.props.filterItems;

        // Search result in buildings
        const prevChurches = prevProps.churches;
        const thisChurches = this.props.churches;

        // Search based on location
        const prevUserLocation = prevProps.userLocation;
        const thisUserLocation = this.props.userLocation;

        if (
            prevUserLocation !== thisUserLocation &&
            thisUserLocation !== null
        ) {
            this.getChurchesByLocation(thisUserLocation);
        }

        if (prevSearchTerm !== thisSearchTerm && thisSearchTerm !== null) {
            this.scheduleFilterSearch(thisSearchTerm, thisFilterItems);
        }

        if (prevFilterItems !== thisFilterItems && thisFilterItems) {
            this.scheduleFilterSearch(thisSearchTerm, thisFilterItems);
        }

        if (prevChurches !== thisChurches && thisChurches) {
            this.setState({
                churchData: thisChurches,
                loading: false,
                loadingMore: false,
            });
        }
    }

    componentWillUnmount() {
        if (this._searchDebounceTimer) {
            clearTimeout(this._searchDebounceTimer);
            this._searchDebounceTimer = null;
        }
        if (this._cancelTokenSource) {
            this._cancelTokenSource.cancel();
            this._cancelTokenSource = null;
        }
    }

    // ================================
    // Functions to fetch content/data
    // ================================

    getChurchesByLocation(latlon) {
        this.setState({ loading: true });

        // @note: The default radius value on the backend is 30. 
        // If this needs to be updated, add `&radius=<your_value>` to the call
        const options = `?lat=${latlon.lat}&lng=${latlon.lng}`;

        axios
            .get(`${API_URL}/api/churches/nearby${options}`, ApiConfig)
            .then((res) => {
                // Update Redux store
                this.props.setLocationsNear(res.data.churches, res.data.nextPage, res.data.totalRecords);
                this.setState({
                    loading: false,
                    churchData: res.data.churches,
                });
            })
            .catch((error) => {
                if (error && error.response && error.response.status === 429) {
                    this.setState({ loading: false });
                    this.props.setSearchError(
                        "For mange søk på kort tid. Vent litt og prøv igjen."
                    );
                    return;
                }

                console.error("Error \"likeved\"", error);
            });
    }

    scheduleFilterSearch(searchTerm, filters) {
        if (this._searchDebounceTimer) {
            clearTimeout(this._searchDebounceTimer);
        }

        this.setState({ loading: true });

        this._searchDebounceTimer = setTimeout(() => {
            this._searchDebounceTimer = null;
            this.getChurchesByName(searchTerm, filters, true);
        }, SEARCH_DEBOUNCE_MS);
    }

    getChurchesByName(searchTerm, filters, reload, pageSize, from) {
        // If no filter or term is present, clear state and return null
        if (
            !this.props.searchTerm &&
            (this.props.filterItems.length === 0 ||
                this.props.filterItems.size === 0)
        ) {
            this.setState({ churchData: null, loading: false, loadingMore: false });
            return null;
        }

        if (reload) this.setState({ loading: true });
        else this.setState({ loadingMore: true });

        // Getting the correct values
        function filterAndCleanItems(type, useKey = false) {
            const filtered = filters.filter((item) => item.type === type);
            if (useKey) return filtered.map((item) => item.key);
            else return filtered.map((item) => item.value);
        }

        const countyNames = filters && filterAndCleanItems("County");
        const eras = filters && filterAndCleanItems("Era", true);
        const protectionStatus =
            filters && filterAndCleanItems("ProtectionStatus", true);
        const constructionMaterials =
            filters && filterAndCleanItems("Construction");
        const municipalityNames = filters && filterAndCleanItems("Municipality");

        const options = {
            filters: {
                searchPhrase: searchTerm,
                eras: [...eras],
                countyNames: [...countyNames],
                constructionMaterials: [...constructionMaterials],
                protectionStatuses: [...protectionStatus],
                municipalitiesNames: [...municipalityNames],
            },
            page: from ? from : 1,
            pageSize: pageSize ? pageSize : 9,
        };

        if (this._cancelTokenSource) {
            this._cancelTokenSource.cancel();
        }
        this._cancelTokenSource = axios.CancelToken.source();

        axios
            .post(`${API_URL}/api/churches/filter`, options, { ...ApiConfig, cancelToken: this._cancelTokenSource.token })
            .then((res) => {
                // Update Redux store
                this.props.setSearchResult(
                    { result: res.data.churches, term: this.props.searchTerm },
                    res.data.nextPage,
                    res.data.totalRecords,
                    reload
                );
            })
            .catch((error) => {
                if (axios.isCancel(error)) {
                    return;
                }

                if (error && error.response && error.response.status === 429) {
                    this.setState({ loading: false, loadingMore: false });
                    this.props.setSearchError(
                        "For mange søk på kort tid. Vent litt og prøv igjen."
                    );
                    return;
                }

                this.setState({ loading: false, loadingMore: false });
                console.error("FIN Error", error);
                this.props.setSearchError(
                    "Det oppstod en feil ved henting av kirker. Prøv å laste siden på nytt."
                );
            });
    }

    // ================================
    // Functions dealing with data
    // ================================

    findEraFromUrl() {
        const { history, filters } = this.props;

        const locationQuery = history.location.search;
        const value = locationQuery.split("=").pop();
        const key = filters.eras.find((item) => item.value === value).key;

        // Update Redux which will trigger new fetch
        this.props.setFilterItems([{ value, key, type: "Era" }]);
    }

    findCurrentEra(name) {
        if (name === "Middelalder") return { from: 0, to: 1599 };
        if (name === "1600-tallet") return { from: 1600, to: 1699 };
        if (name === "1700-tallet") return { from: 1700, to: 1799 };
        if (name === "1800-tallet") return { from: 1800, to: 1899 };
        if (name === "1900-tallet") return { from: 1900, to: 1999 };
        if (name === "2000-tallet") return { from: 2000, to: 2099 };
        return { from: 0, to: 2099 };
    }

    loadMore() {
        if (this._searchDebounceTimer) {
            clearTimeout(this._searchDebounceTimer);
            this._searchDebounceTimer = null;
        }

        const { churchData } = this.state;
        const { searchTerm, filterItems, totalAvailable} = this.props;
        const term = searchTerm;
        const from = this.props.nextPage;
        let pageSize = 9;

        if ((totalAvailable - churchData.length) < 9) pageSize = totalAvailable - churchData.length;

        this.getChurchesByName(term, filterItems, false, pageSize, from);
    }

    // Every time a filter option is clicked
    filterOptionSelect(value, key) {
        const { filterItems } = this.props;

        const newFilterData = filterItems.filter((item) => item.key !== key);

        this.props.setFilterItems(newFilterData);
    }

    // When a new era is chosen in the EraBox slider (arrows or click on a year)
    eraSelect(era) {
        const { filterItems, filters } = this.props;

        if (!era || !filters || !filters.eras) return;

        // The slider also moves when the era filter changes, so skip if it's already selected
        const currentEra = filterItems.find((item) => item.type === "Era");
        if (currentEra && currentEra.value === era.fields.title) return;

        const eraFilter = filters.eras.find(
            (item) => item.value === era.fields.title
        );
        if (!eraFilter) return;

        // Replace the era, same as the radio buttons in the filter
        const otherItems = filterItems.filter((item) => item.type !== "Era");
        this.props.setFilterItems([
            { value: eraFilter.value, key: eraFilter.key, type: "Era" },
            ...otherItems,
        ]);
    }

    // ================================
    // Functions rendering content
    // ================================

    printSearchFilters() {
        const { filterItems } = this.props;

        if (!filterItems) return;

        return filterItems.map((data) => (
            <div key={data.key} className="filter button button--gray">
                {data.value}
                <i
                    onClick={() =>
                        this.filterOptionSelect(data.value, data.key, false)
                    }
                    className="filter__close-icon"
                    aria-hidden="true"
                ></i>
            </div>
        ));
    }

    printLoadMoreButton() {
        const { churchData, loadingMore, loading } = this.state;

        // Dont show if no churches exists or if loading is true
        if (churchData === null || loadingMore || loading) return null;
        if (this.props.nextPage === -1)
            return (
                <Button color="button--gray" size="large" center>
                    Ingen flere treff å laste{" "}
                </Button>
            );

        return (
            <Button
                size="large"
                color="button--purple"
                center
                onClick={this.loadMore}
            >
                Last flere{" "}
                {`(${churchData.length}/${this.props.totalAvailable})`}
            </Button>
        );
    }

    render() {
        const { churchData, loading, loadingMore } = this.state;
        const { filterItems, eras, searchError } = this.props;

        // Determens what type of info-box to show or not
        const status = churchData
            ? churchData.length > 0
                ? "vis"
                : "error"
            : "tips";

        // Determens wheter nor not to show the Eara-box and which era to be in focus
        const eraExists = filterItems
            ? filterItems.find((item) => item.type === "Era")
            : false;
        const eraId =
            eraExists && eras
                ? eras.find((item) => item.fields.title === eraExists.value)
                    .fields.id
                : false;

        return (
            <DocumentTitle title={`Søk ${MetaTitle}`}>
                <div className="search">
                    <MediumSpacer />

                    <div className="container">
                        <div className="search-filters">
                            {this.printSearchFilters()}
                        </div>

                        <MediumSpacer />
                        <h2 className="page-title">
                            <img
                                className="page-title__icon"
                                src={SearchChurch}
                                alt="Ikon for søkeresultater"
                            />
                            {churchData
                                ? `Viser ${
                                    churchData ? churchData.length : 0
                                } av ${this.props.totalAvailable} `
                                : "Søkeresultater"}
                        </h2>

                        <MediumSpacer />

                        {eras && eraId && (
                            <EraBox
                                activeEra={eraId}
                                onEraChange={(era) => this.eraSelect(era)}
                            />
                        )}
                        {eras && eraId && <MediumSpacer />}

                        {status === "error" && !loading && !searchError && (
                            <div className="info-box info-box--red">
                                <span>Desverre ingen treff på søket ditt.</span>
                            </div>
                        )}
                        {status === "tips" && !loading && !searchError && (
                            <div className="info-box info-box--green">
                                <span>Søk etter kirker.</span>
                            </div>
                        )}

                        <SearchResult
                            data={churchData}
                            error={searchError}
                            loading={loading}
                            loadingMore={loadingMore}
                        />

                        {churchData && !loading && <MediumSpacer />}
                        {churchData && churchData.length < this.props.totalAvailable && (
                            <div className="center-wrapper">
                                {this.printLoadMoreButton()}
                            </div>
                        )}
                    </div>

                    <BigSpacer />
                </div>
            </DocumentTitle>
        );
    }
}

const mapStateToProps = createStructuredSelector({
    searchTerm: selectSearchTerm(),
    searchResult: selectSearchResult(),
    churchesNear: selectChurchesNear(),
    userLocation: selectUserLocation(),
    filterItems: selectFilterItems(),
    churches: selectChurches(),
    nextPage: selectNextPage(),
    totalAvailable: selectTotalAvailable(),
    fetchAgain: selectFetchStatus(),
    filters: selectFilters(),
    eras: selectEraContent(),
    latestFetchType: selectLatestFetchType(),
    searchError: selectSearchError(),
});

const mapDispatchToProps = (dispatch) => {
    return {
        setLocations: (locations) => dispatch(actions.setLocations(locations)),
        setLocationsNear: (locations, nextPage, totalAmount) =>
            dispatch(
                actions.setLocationsNear(locations, nextPage, totalAmount)
            ),
        setFilterItems: (items) => dispatch(actions.setFilterItems(items)),
        setSearchResult: (result, nextPage, totalAvailable, reload) =>
            dispatch(
                actions.setSearchResult(
                    result,
                    nextPage,
                    totalAvailable,
                    reload
                )
            ),
        setSearchError: (message) => dispatch(actions.setSearchError(message)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Search);



// WEBPACK FOOTER //
// ./src/containers/Search/index.js